// Updated useTimerData.js with CRUD functions and state management
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useTenant } from '../contexts/TenantContext'
import { useAuth } from '../contexts/AuthContext'

export const useTimerData = () => {
  const [clients, setClients] = useState([])
  const [projects, setProjects] = useState([])
  const [timeEntries, setTimeEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const { currentTenant } = useTenant()
  const { user } = useAuth()

  useEffect(() => {
    if (currentTenant && user) {
      loadData()
    }
  }, [currentTenant?.id, user?.id])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Load clients
      const { data: clientsData } = await supabase
        .from('clients')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'active')
        .order('name')

      // Load projects  
      const { data: projectsData } = await supabase
        .from('projects')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'active')
        .order('name')

      // Load recent time entries (last 30 days)
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      
      const { data: entriesData } = await supabase
        .from('time_entries')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .eq('user_id', user.id)
        .gte('entry_date', thirtyDaysAgo.toISOString().split('T')[0])
        .order('entry_date', { ascending: false })
        .limit(100) // Increased limit for better calendar experience

      setClients(clientsData || [])
      setProjects(projectsData || [])
      setTimeEntries(entriesData || [])
      
    } catch (error) {
      console.error('Error loading timer data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Create a new time entry and update local state
  const createTimeEntry = async (entryData) => {
    try {
      const newEntry = {
        user_id: user.id,
        tenant_id: currentTenant.id,
        ...entryData,
        status: entryData.status || 'draft'
      }

      const { data, error } = await supabase
        .from('time_entries')
        .insert([newEntry])
        .select()
        .single()

      if (error) throw error

      // Update local state immediately (optimistic update)
      setTimeEntries(prev => [data, ...prev])
      
      return { data, error: null }
    } catch (error) {
      console.error('Error creating time entry:', error)
      return { data: null, error: error.message }
    }
  }

  // Update a time entry and update local state
  const updateTimeEntry = async (entryId, updates) => {
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .update(updates)
        .eq('id', entryId)
        .select()
        .single()

      if (error) throw error

      // Update local state immediately (optimistic update)
      setTimeEntries(prev => 
        prev.map(entry => 
          entry.id === entryId ? { ...entry, ...data } : entry
        )
      )
      
      return { data, error: null }
    } catch (error) {
      console.error('Error updating time entry:', error)
      return { data: null, error: error.message }
    }
  }

  // Delete a time entry and update local state
  const deleteTimeEntry = async (entryId) => {
    try {
      const { error } = await supabase
        .from('time_entries')
        .delete()
        .eq('id', entryId)

      if (error) throw error

      // Update local state immediately (optimistic update)
      setTimeEntries(prev => prev.filter(entry => entry.id !== entryId))
      
      return { error: null }
    } catch (error) {
      console.error('Error deleting time entry:', error)
      return { error: error.message }
    }
  }

  return {
    clients,
    projects,
    timeEntries,
    loading,
    createTimeEntry,
    updateTimeEntry,
    deleteTimeEntry,
    refetch: loadData
  }
}